import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useLocationStore } from '@/stores/useLocationStore';
import { useLocations } from '@/hooks/useLocations';
import { parseDateParam, sanitizeLocationDates } from '@/lib/locationDates';

export const useUrlHydration = () => {
    const [searchParams] = useSearchParams();
    const { setLocation, setDateRange } = useLocationStore();
    const { data: locations } = useLocations();

    useEffect(() => {
        const locationParam = searchParams.get('location');
        const startParam = searchParams.get('start');
        const endParam = searchParams.get('end');

        // Only hydrate if we have at least one parameter
        if (!locationParam && !startParam && !endParam) return;

        if (locations && locationParam) {
            // Try to find by ID first, then slug, then name (case-insensitive)
            const foundLocation = locations.find(l =>
                l.id === locationParam ||
                l.slug === locationParam ||
                l.name.toLowerCase() === locationParam.toLowerCase() ||
                l.city?.toLowerCase() === locationParam.toLowerCase()
            );

            if (foundLocation) {
                setLocation(foundLocation);
            }
        }

        if (startParam || endParam) {
            // Incomplete date param sets are treated as invalid and always cleared.
            if (!startParam || !endParam) {
                setDateRange({ from: undefined, to: undefined });
                return;
            }

            const startDate = parseDateParam(startParam);
            const endDate = parseDateParam(endParam);

            if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                const sanitized = sanitizeLocationDates({ from: startDate, to: endDate }, null);
                setDateRange(sanitized.dateRange);
            } else {
                setDateRange({ from: undefined, to: undefined });
            }
        }
    }, [searchParams, locations, setLocation, setDateRange]);
};
